import { merge, NEVER, Observable, of } from 'rxjs';
import { map, catchError, startWith, switchMap } from 'rxjs/operators';

interface Pending {
	state: 'pending';
	value: undefined;
}

interface Error {
	state: 'error';
	value: undefined;
}

interface Loaded<T> {
	state: 'loaded';
	value: T;
}

export type LoadingState<T> = Pending | Error | Loaded<T>;

export function withLoadingState<T>() {
	return (source: Observable<T>) =>
		new Observable<LoadingState<T>>((subscriber) => {
			return source
				.pipe(
					map<T, Loaded<T>>((value) => ({ state: 'loaded', value })),
					catchError((error) => {
						console.error(error);
						return of({ state: 'error' } as Error);
					}),
					startWith({ state: 'pending' } as Pending)
				)
				.subscribe({
					next: (v) => subscriber.next(v),
					error: (e) => subscriber.error(e),
					complete: () => subscriber.complete(),
				});
		});
}

export function asLoadingState<T>(
	source: Observable<T>,
	refresh: Observable<void> = NEVER
) {
	const loadingSource = source.pipe(withLoadingState());
	return new Observable<LoadingState<T>>((subscriber) => {
		return merge(
			loadingSource,
			refresh.pipe(switchMap(() => loadingSource))
		).subscribe({
			next: (v) => subscriber.next(v),
			error: (e) => subscriber.error(e),
			complete: () => subscriber.complete(),
		});
	});
}
